var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import classNames from 'classnames';
import Overflow from 'rc-overflow';
import warning from 'rc-util/lib/warning';
import KeyCode from 'rc-util/lib/KeyCode';
import omit from 'rc-util/lib/omit';
import { MenuContext } from './context/MenuContext';
import useActive from './hooks/useActive';
import { warnItemProp } from './utils/warnUtil';
import Icon from './Icon';
import useDirectionStyle from './hooks/useDirectionStyle';
import { useFullPath, useMeasure } from './context/PathContext';
import { useMenuId } from './context/IdContext';
// Since Menu event provide the `info.item` which point to the MenuItem node instance.
// We have to use class component here.
// This should be removed from doc & api in future.
class LegacyMenuItem extends React.Component {
    render() {
        const _a = this.props, { title, attribute, elementRef } = _a, restProps = __rest(_a, ["title", "attribute", "elementRef"]);
        const passedProps = omit(restProps, ['eventKey']);
        warning(!attribute, '`attribute` of Menu.Item is deprecated. Please pass attribute directly.');
        return (<Overflow.Item {...attribute} title={typeof title === 'string' ? title : undefined} {...passedProps} ref={elementRef}/>);
    }
}
/**
 * Real Menu Item component
 */
const InternalMenuItem = (props) => {
    const { style, className, eventKey, sub, warnKey, disabled, itemIcon, children, 
    // Aria
    role, 
    // Active
    onMouseEnter, onMouseLeave, onClick, onKeyDown, onFocus } = props, restProps = __rest(props, ["style", "className", "eventKey", "sub", "warnKey", "disabled", "itemIcon", "children", "role", "onMouseEnter", "onMouseLeave", "onClick", "onKeyDown", "onFocus"]);
    const domDataId = useMenuId(eventKey);
    const { prefixCls, onItemClick, disabled: contextDisabled, overflowDisabled, 
    // Icon
    itemIcon: contextItemIcon, 
    // Select
    selectedKeys, 
    // Active
    onActive } = React.useContext(MenuContext);
    const itemCls = `${prefixCls}-item`;
    const legacyMenuItemRef = React.useRef();
    const elementRef = React.useRef();
    const mergedDisabled = contextDisabled || disabled;
    const connectedKeys = useFullPath(eventKey);
    // ================================ Warn ================================
    if (process.env.NODE_ENV !== 'production' && warnKey) {
        warning(false, 'MenuItem should not leave undefined `key`.');
    }
    // ============================= Info =============================
    const getEventInfo = (e) => {
        return {
            key: eventKey,
            // Note: For legacy code is reversed which not like other antd component
            keyPath: [...connectedKeys].reverse(),
            item: legacyMenuItemRef.current,
            domEvent: e
        };
    };
    // ============================= Icon =============================
    const mergedItemIcon = itemIcon || contextItemIcon;
    // ============================ Active ============================
    const _a = useActive(eventKey, mergedDisabled, onMouseEnter, onMouseLeave), { active } = _a, activeProps = __rest(_a, ["active"]);
    // ============================ Select ============================
    const selected = selectedKeys.includes(eventKey);
    // ======================== DirectionStyle ========================
    const directionStyle = useDirectionStyle(className, sub);
    // ============================ Events ============================
    const onInternalClick = e => {
        if (mergedDisabled) {
            return;
        }
        const info = getEventInfo(e);
        onClick === null || onClick === void 0 ? void 0 : onClick(warnItemProp(info));
        onItemClick(info);
    };
    const onInternalKeyDown = e => {
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        if (e.which === KeyCode.ENTER) {
            const info = getEventInfo(e);
            // Legacy. Key will also trigger click event
            onClick === null || onClick === void 0 ? void 0 : onClick(warnItemProp(info));
            onItemClick(info);
        }
    };
    /**
     * Used for accessibility. Helper will focus element without key board.
     * We should manually trigger an active
     */
    const onInternalFocus = e => {
        onActive(eventKey);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    };
    // ============================ Render ============================
    const optionRoleProps = {};
    if (props.role === 'option') {
        optionRoleProps['aria-selected'] = selected;
    }
    return (<LegacyMenuItem ref={legacyMenuItemRef} elementRef={elementRef} role={role === null ? 'none' : role || 'menuitem'} tabIndex={disabled ? null : -1} data-menu-id={overflowDisabled && domDataId ? null : domDataId} {...restProps} {...activeProps} {...optionRoleProps} component="li" aria-disabled={disabled} style={Object.assign(Object.assign({}, directionStyle), style)} className={classNames(itemCls, {
            [`${itemCls}-active`]: active,
            [`${itemCls}-selected`]: selected,
            [`${itemCls}-disabled`]: mergedDisabled
        }, className)} onClick={onInternalClick} onKeyDown={onInternalKeyDown} onFocus={onInternalFocus}>
            {children}
            <Icon props={Object.assign(Object.assign({}, props), { isSelected: selected })} icon={mergedItemIcon}/>
        </LegacyMenuItem>);
};
function MenuItem(props) {
    const { eventKey } = props;
    // ==================== Record KeyPath ====================
    const measure = useMeasure();
    const connectedKeyPath = useFullPath(eventKey);
    // eslint-disable-next-line consistent-return
    React.useEffect(() => {
        if (measure) {
            measure.registerPath(eventKey, connectedKeyPath);
            return () => {
                measure.unregisterPath(eventKey, connectedKeyPath);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectedKeyPath]);
    if (measure) {
        return null;
    }
    // ======================== Render ========================
    return <InternalMenuItem {...props}/>;
}
export default MenuItem;
